import { __rest } from "tslib";
import * as React from 'react';
import { useState, useRef, useEffect, useMemo } from 'react';
import KeyCode from 'rc-util/lib/KeyCode';
import isMobile from 'rc-util/lib/isMobile';
import classNames from 'classnames';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import Selector from './Selector';
import SelectTrigger from './SelectTrigger';
import { INTERNAL_PROPS_MARK } from './interface/generator';
import { toInnerValue, toOuterValues, getUUID } from './utils/commonUtil';
import TransBtn from './TransBtn';
import useLock from './hooks/useLock';
import useDelayReset from './hooks/useDelayReset';
import useLayoutEffect from './hooks/useLayoutEffect';
import { getSeparatedContent } from './utils/valueUtil';
import useSelectTriggerControl from './hooks/useSelectTriggerControl';
import useCacheDisplayValue from './hooks/useCacheDisplayValue';
import useCacheOptions from './hooks/useCacheOptions';
const DEFAULT_OMIT_PROPS = [
    'removeIcon',
    'placeholder',
    'autoFocus',
    'maxTagCount',
    'maxTagTextLength',
    'maxTagPlaceholder',
    'choiceTransitionName',
    'onInputKeyDown',
    'tabIndex',
];
export default function generateSelector(config) {
    const { prefixCls: defaultPrefixCls, components: { optionList: OptionList }, convertChildrenToData, flattenOptions, getLabeledValue, filterOptions, isValueDisabled, findValueOption, warningProps, fillOptionsWithMissingValue, omitDOMProps, } = config;
    function Select(props, ref) {
        const { prefixCls = defaultPrefixCls, className, id, open, defaultOpen, options, children, mode, value, defaultValue, labelInValue, showSearch, inputValue, searchValue, filterOption, filterSort, optionFilterProp = 'value', autoClearSearchValue = true, onSearch, onPaste, allowClear, clearIcon, showArrow, inputIcon, menuItemSelectedIcon, disabled, loading, defaultActiveFirstOption, notFoundContent = 'Not Found', optionLabelProp, backfill, tabIndex, getInputElement, getPopupContainer, listHeight = 200, listItemHeight = 20, animation, transitionName, virtual, dropdownStyle, dropdownClassName, dropdownMatchSelectWidth, dropdownRender, dropdownAlign, showAction = [], direction, tokenSeparators, tagRender, onPopupScroll, onDropdownVisibleChange, onFocus, onBlur, onKeyUp, onKeyDown, onMouseDown, onChange, onDelete, onSelect, onDeselect, onClear, internalProps = {} } = props, restProps = __rest(props, ["prefixCls", "className", "id", "open", "defaultOpen", "options", "children", "mode", "value", "defaultValue", "labelInValue", "showSearch", "inputValue", "searchValue", "filterOption", "filterSort", "optionFilterProp", "autoClearSearchValue", "onSearch", "onPaste", "allowClear", "clearIcon", "showArrow", "inputIcon", "menuItemSelectedIcon", "disabled", "loading", "defaultActiveFirstOption", "notFoundContent", "optionLabelProp", "backfill", "tabIndex", "getInputElement", "getPopupContainer", "listHeight", "listItemHeight", "animation", "transitionName", "virtual", "dropdownStyle", "dropdownClassName", "dropdownMatchSelectWidth", "dropdownRender", "dropdownAlign", "showAction", "direction", "tokenSeparators", "tagRender", "onPopupScroll", "onDropdownVisibleChange", "onFocus", "onBlur", "onKeyUp", "onKeyDown", "onMouseDown", "onChange", "onDelete", "onSelect", "onDeselect", "onClear", "internalProps"]);
        const useInternalProps = internalProps.mark === INTERNAL_PROPS_MARK;
        const domProps = omitDOMProps ? omitDOMProps(restProps) : restProps;
        DEFAULT_OMIT_PROPS.forEach(prop => {
            delete domProps[prop];
        });
        const containerRef = useRef(null);
        const triggerRef = useRef(null);
        const selectorRef = useRef(null);
        const listRef = useRef(null);
        const tokenWithEnter = useMemo(() => (tokenSeparators || []).some(tokenSeparator => ['\n', '\r\n'].includes(tokenSeparator)), [tokenSeparators]);
        const [mockFocused, setMockFocused, cancelSetMockFocused] = useDelayReset();
        const [innerId, setInnerId] = useState();
        useEffect(() => {
            setInnerId(`rc_select_${getUUID()}`);
        }, []);
        const mergedId = id || innerId;
        let mergedOptionLabelProp = optionLabelProp;
        if (mergedOptionLabelProp === undefined) {
            mergedOptionLabelProp = options ? 'label' : 'children';
        }
        const mergedLabelInValue = mode === 'combobox' ? false : labelInValue;
        const isMultiple = mode === 'tags' || mode === 'multiple';
        const mergedShowSearch = showSearch !== undefined ? showSearch : isMultiple || mode === 'combobox';
        const [mobile, setMobile] = useState(false);
        useEffect(() => {
            setMobile(isMobile());
        }, []);
        const selectorDomRef = useRef(null);
        React.useImperativeHandle(ref, () => {
            var _a, _b, _c;
            return ({
                focus: (_a = selectorRef === null || selectorRef === void 0 ? void 0 : selectorRef.current) === null || _a === void 0 ? void 0 : _a.focus,
                blur: (_b = selectorRef.current) === null || _b === void 0 ? void 0 : _b.blur,
                scrollTo: (_c = listRef.current) === null || _c === void 0 ? void 0 : _c.scrollTo,
            });
        });
        const [mergedValue, setMergedValue] = useMergedState(defaultValue, {
            value,
        });
        const [mergedRawValue, mergedValueMap] = useMemo(() => toInnerValue(mergedValue, {
            labelInValue: mergedLabelInValue,
            combobox: mode === 'combobox',
        }), [mergedValue, mergedLabelInValue]);
        const rawValues = useMemo(() => new Set(mergedRawValue), [mergedRawValue]);
        const [activeValue, setActiveValue] = useState(null);
        const [innerSearchValue, setInnerSearchValue] = useState('');
        let mergedSearchValue = innerSearchValue;
        if (mode === 'combobox' && mergedValue !== undefined) {
            mergedSearchValue = mergedValue;
        }
        else if (searchValue !== undefined) {
            mergedSearchValue = searchValue;
        }
        else if (inputValue) {
            mergedSearchValue = inputValue;
        }
        const mergedOptions = useMemo(() => {
            let newOptions = options;
            if (newOptions === undefined) {
                newOptions = convertChildrenToData(children);
            }
            if (mode === 'tags' && fillOptionsWithMissingValue) {
                newOptions = fillOptionsWithMissingValue(newOptions, mergedValue, mergedOptionLabelProp, labelInValue);
            }
            return newOptions || [];
        }, [options, children, mode, mergedValue]);
        const mergedFlattenOptions = useMemo(() => flattenOptions(mergedOptions, props), [mergedOptions]);
        const getValueOption = useCacheOptions(mergedFlattenOptions);
        const displayOptions = useMemo(() => {
            if (!mergedSearchValue || !mergedShowSearch) {
                return [...mergedOptions];
            }
            const filteredOptions = filterOptions(mergedSearchValue, mergedOptions, {
                optionFilterProp,
                filterOption: mode === 'combobox' && filterOption === undefined ? () => true : filterOption,
            });
            if (filterSort && Array.isArray(filteredOptions)) {
                return [...filteredOptions].sort(filterSort);
            }
            return filteredOptions;
        }, [mergedOptions, mergedSearchValue, mode, mergedShowSearch, filterSort]);
        const displayFlattenOptions = useMemo(() => flattenOptions(displayOptions, props), [displayOptions]);
        useEffect(() => {
            if (listRef.current && listRef.current.scrollTo) {
                listRef.current.scrollTo(0);
            }
        }, [mergedSearchValue]);
        let displayValues = useMemo(() => {
            const tmpValues = mergedRawValue.map((val) => {
                const valueOptions = getValueOption([val]);
                const displayValue = getLabeledValue(val, {
                    options: valueOptions,
                    prevValueMap: mergedValueMap,
                    labelInValue: mergedLabelInValue,
                    optionLabelProp: mergedOptionLabelProp,
                });
                return Object.assign(Object.assign({}, displayValue), { disabled: isValueDisabled(val, valueOptions) });
            });
            if (!mode && tmpValues.length === 1 && tmpValues[0].value === null && tmpValues[0].label === null) {
                return [];
            }
            return tmpValues;
        }, [mergedValue, mergedOptions, mode]);
        displayValues = useCacheDisplayValue(displayValues);
        const triggerSelect = (newValue, isSelect, source) => {
            const newValueOption = getValueOption([newValue]);
            const outOption = findValueOption([newValue], newValueOption)[0];
            if (!internalProps.skipTriggerSelect) {
                const selectValue = (mergedLabelInValue
                    ? getLabeledValue(newValue, {
                        options: newValueOption,
                        prevValueMap: mergedValueMap,
                        labelInValue: mergedLabelInValue,
                        optionLabelProp: mergedOptionLabelProp,
                    })
                    : newValue);
                if (isSelect && onSelect) {
                    onSelect(selectValue, outOption);
                }
                else if (!isSelect && onDeselect) {
                    onDeselect(selectValue, outOption);
                }
            }
            if (useInternalProps) {
                if (isSelect && internalProps.onRawSelect) {
                    internalProps.onRawSelect(newValue, outOption, source);
                }
                else if (!isSelect && internalProps.onRawDeselect) {
                    internalProps.onRawDeselect(newValue, outOption, source);
                }
            }
        };
        const [prevValueOptions, setPrevValueOptions] = useState([]);
        const triggerChange = (newRawValues, type) => {
            if (useInternalProps && internalProps.skipTriggerChange) {
                return;
            }
            const newRawValuesOptions = getValueOption(newRawValues);
            const outValues = toOuterValues(Array.from(newRawValues), {
                labelInValue: mergedLabelInValue,
                options: newRawValuesOptions,
                getLabeledValue,
                prevValueMap: mergedValueMap,
                optionLabelProp: mergedOptionLabelProp,
            });
            const outValue = (isMultiple ? outValues : outValues[0]);
            if (onChange && (mergedRawValue.length !== 0 || outValues.length !== 0)) {
                const outOptions = findValueOption(newRawValues, newRawValuesOptions, { prevValueOptions });
                setPrevValueOptions(outOptions === null || outOptions === void 0 ? void 0 : outOptions.map((option, index) => {
                    const clone = Object.assign({}, option);
                    Object.defineProperty(clone, '_INTERNAL_OPTION_VALUE_', {
                        get: () => newRawValues[index],
                    });
                    return clone;
                }));
                if (type === 'delete' && onDelete) {
                    onDelete(outValue);
                }
                else {
                    onChange(outValue, isMultiple ? outOptions : outOptions[0]);
                }
            }
            setMergedValue(outValue);
        };
        const onInternalSelect = (newValue, { selected, source }) => {
            if (disabled) {
                return;
            }
            let newRawValue;
            if (isMultiple) {
                newRawValue = new Set(mergedRawValue);
                if (selected) {
                    if (newRawValue.has(newValue)) {
                        newRawValue = Array.from(newRawValue);
                        newRawValue.push(newValue);
                    }
                    else {
                        newRawValue.add(newValue);
                    }
                }
                else {
                    newRawValue.delete(newValue);
                }
            }
            else {
                newRawValue = new Set();
                newRawValue.add(newValue);
            }
            if (isMultiple || (!isMultiple && Array.from(mergedRawValue)[0] !== newValue)) {
                triggerChange(Array.from(newRawValue));
            }
            triggerSelect(newValue, !isMultiple || selected, source);
            if (mode === 'combobox') {
                setInnerSearchValue(String(newValue));
                setActiveValue('');
            }
            else if (!isMultiple || autoClearSearchValue) {
                setInnerSearchValue('');
                setActiveValue('');
            }
        };
        const onInternalOptionSelect = (newValue, info) => {
            onInternalSelect(newValue, Object.assign(Object.assign({}, info), { source: 'option' }));
        };
        const onInternalSelectionSelect = (newValue, info) => {
            onInternalSelect(newValue, Object.assign(Object.assign({}, info), { source: 'selection' }));
        };
        const customizeInputElement = (mode === 'combobox' && getInputElement && getInputElement()) || null;
        const [innerOpen, setInnerOpen] = useMergedState(undefined, {
            defaultValue: defaultOpen,
            value: open,
        });
        let mergedOpen = innerOpen;
        const emptyListContent = !notFoundContent && !displayOptions.length;
        if (disabled || (emptyListContent && mergedOpen && mode === 'combobox')) {
            mergedOpen = false;
        }
        const triggerOpen = emptyListContent ? false : mergedOpen;
        const onToggleOpen = (newOpen) => {
            const nextOpen = newOpen !== undefined ? newOpen : !mergedOpen;
            if (innerOpen !== nextOpen && !disabled) {
                setInnerOpen(nextOpen);
                if (onDropdownVisibleChange) {
                    onDropdownVisibleChange(nextOpen);
                }
            }
        };
        useSelectTriggerControl([containerRef.current, triggerRef.current && triggerRef.current.getPopupElement()], triggerOpen, onToggleOpen);
        const triggerSearch = (searchText, fromTyping, isCompositing) => {
            let ret = true;
            let newSearchText = searchText;
            setActiveValue(null);
            const patchLabels = isCompositing ? null : getSeparatedContent(searchText, tokenSeparators);
            let patchRawValues = patchLabels;
            if (mode === 'combobox') {
                if (fromTyping) {
                    triggerChange([newSearchText]);
                }
            }
            else if (patchLabels) {
                newSearchText = '';
                if (mode !== 'tags') {
                    patchRawValues = patchLabels
                        .map(label => {
                        const item = mergedFlattenOptions.find(({ data }) => data[mergedOptionLabelProp] === label);
                        return item ? item.data.value : null;
                    })
                        .filter((val) => val !== null);
                }
                const newRawValues = Array.from(new Set([...mergedRawValue, ...patchRawValues]));
                triggerChange(newRawValues);
                newRawValues.forEach(newRawValue => {
                    triggerSelect(newRawValue, true, 'input');
                });
                onToggleOpen(false);
                ret = false;
            }
            setInnerSearchValue(newSearchText);
            if (onSearch && mergedSearchValue !== newSearchText) {
                onSearch(newSearchText);
            }
            return ret;
        };
        const onSearchSubmit = (searchText) => {
            if (!searchText || !searchText.trim()) {
                return;
            }
            const newRawValues = Array.from(new Set([...mergedRawValue, searchText]));
            triggerChange(newRawValues);
            newRawValues.forEach(newRawValue => {
                triggerSelect(newRawValue, true, 'input');
            });
            setInnerSearchValue('');
        };
        useEffect(() => {
            if (innerOpen && !!disabled) {
                setInnerOpen(false);
            }
        }, [disabled]);
        useEffect(() => {
            if (!mergedOpen && !isMultiple && mode !== 'combobox') {
                triggerSearch('', false, false);
            }
        }, [mergedOpen]);
        const [getClearLock, setClearLock] = useLock();
        const onInternalKeyDown = (event, ...rest) => {
            var _a, _b, _c;
            const clearLock = getClearLock();
            const { which } = event;
            if (which === KeyCode.ENTER) {
                if (mode !== 'combobox') {
                    event.preventDefault();
                }
                if (!mergedOpen) {
                    onToggleOpen(true);
                }
            }
            setClearLock(!!mergedSearchValue);
            if ([KeyCode.DELETE, KeyCode.BACKSPACE].includes(which) && !clearLock && isMultiple && !mergedSearchValue && mergedRawValue.length) {
                const chosenList = (_a = selectorRef === null || selectorRef === void 0 ? void 0 : selectorRef.current) === null || _a === void 0 ? void 0 : _a.getChosenList();
                if (chosenList.length) {
                    const newValues = mergedRawValue.filter(val => chosenList.indexOf(val) < 0);
                    const index = mergedRawValue.indexOf(chosenList[chosenList.length - 1]);
                    if (index >= 0) {
                        const newIndex = newValues.length - index;
                        selectorRef.current.setInputIndex(newIndex >= 0 ? newIndex : 0);
                    }
                    const removedValue = [...chosenList];
                    triggerChange(newValues, 'delete');
                    removedValue.forEach(val => {
                        triggerSelect(val, false, 'input');
                    });
                    (_b = selectorRef === null || selectorRef === void 0 ? void 0 : selectorRef.current) === null || _b === void 0 ? void 0 : _b.clearChosenList();
                    setTimeout(() => {
                        var _a;
                        (_a = selectorRef === null || selectorRef === void 0 ? void 0 : selectorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    });
                }
                else {
                    const offset = which === KeyCode.BACKSPACE ? 0 : 1;
                    const index = selectorRef.current.getLastEnabledIndex() + offset;
                    if (index >= 0 && index < mergedRawValue.length) {
                        const chosenValue = mergedRawValue[index];
                        (_c = selectorRef === null || selectorRef === void 0 ? void 0 : selectorRef.current) === null || _c === void 0 ? void 0 : _c.setChosenValue([chosenValue]);
                    }
                }
            }
            if (mergedOpen && listRef.current) {
                listRef.current.onKeyDown(event, ...rest);
            }
            selectorRef.current.onKeyDown(event, ...rest);
            if (onKeyDown) {
                onKeyDown(event, ...rest);
            }
        };
        const onInternalKeyUp = (event, ...rest) => {
            if (mergedOpen && listRef.current) {
                listRef.current.onKeyUp(event, ...rest);
            }
            if (onKeyUp) {
                onKeyUp(event, ...rest);
            }
        };
        const focusRef = useRef(false);
        const onContainerFocus = (...args) => {
            setMockFocused(true);
            if (!disabled) {
                if (onFocus && !focusRef.current) {
                    onFocus(...args);
                }
                if (showAction.includes('focus')) {
                    onToggleOpen(true);
                }
            }
            focusRef.current = true;
        };
        const onContainerBlur = (...args) => {
            var _a;
            setMockFocused(false, () => {
                focusRef.current = false;
                onToggleOpen(false);
            });
            if (disabled) {
                return;
            }
            (_a = selectorRef === null || selectorRef === void 0 ? void 0 : selectorRef.current) === null || _a === void 0 ? void 0 : _a.clearChosenList();
            if (mergedSearchValue) {
                if (mode === 'tags') {
                    triggerSearch('', false, false);
                    triggerChange(Array.from(new Set([...mergedRawValue, mergedSearchValue])));
                }
                else if (mode === 'multiple') {
                    setInnerSearchValue('');
                }
            }
            if (onBlur) {
                onBlur(...args);
            }
        };
        const activeTimeoutIds = [];
        useEffect(() => () => {
            activeTimeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
            activeTimeoutIds.splice(0, activeTimeoutIds.length);
        }, []);
        const onInternalMouseDown = (event, ...restArgs) => {
            const { target } = event;
            const popupElement = triggerRef.current && triggerRef.current.getPopupElement();
            if (popupElement && popupElement.contains(target)) {
                const timeoutId = setTimeout(() => {
                    var _a;
                    const index = activeTimeoutIds.indexOf(timeoutId);
                    if (index !== -1) {
                        activeTimeoutIds.splice(index, 1);
                    }
                    cancelSetMockFocused();
                    if (!mobile && !popupElement.contains(document.activeElement)) {
                        (_a = selectorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                });
                activeTimeoutIds.push(timeoutId);
            }
            if (onMouseDown) {
                onMouseDown(event, ...restArgs);
            }
        };
        const [accessibilityIndex, setAccessibilityIndex] = useState(0);
        const mergedDefaultActiveFirstOption = defaultActiveFirstOption !== undefined ? defaultActiveFirstOption : mode !== 'combobox';
        const onActiveValue = (active, index, { source = 'keyboard' } = {}) => {
            setAccessibilityIndex(index);
            if (backfill && mode === 'combobox' && active !== null && source === 'keyboard') {
                setActiveValue(String(active));
            }
        };
        const [containerWidth, setContainerWidth] = useState(null);
        const [, forceUpdate] = useState({});
        function onPopupMouseEnter() {
            forceUpdate({});
        }
        useLayoutEffect(() => {
            if (triggerOpen) {
                const newWidth = Math.ceil(containerRef.current.offsetWidth);
                if (containerWidth !== newWidth) {
                    setContainerWidth(newWidth);
                }
            }
        }, [triggerOpen]);
        const popupNode = (React.createElement(OptionList, { ref: listRef, prefixCls: prefixCls, id: mergedId, open: mergedOpen, childrenAsData: !options, options: displayOptions, flattenOptions: displayFlattenOptions, multiple: isMultiple, values: rawValues, height: listHeight, itemHeight: listItemHeight, onSelect: onInternalOptionSelect, onToggleOpen: onToggleOpen, onActiveValue: onActiveValue, defaultActiveFirstOption: mergedDefaultActiveFirstOption, notFoundContent: notFoundContent, onScroll: onPopupScroll, searchValue: mergedSearchValue, menuItemSelectedIcon: menuItemSelectedIcon, virtual: virtual !== false && dropdownMatchSelectWidth !== false, onMouseEnter: onPopupMouseEnter }));
        let clearNode;
        const onClearMouseDown = () => {
            if (useInternalProps && internalProps.onClear) {
                internalProps.onClear();
            }
            if (onClear) {
                onClear();
            }
            triggerChange([]);
            triggerSearch('', false, false);
        };
        if (!disabled && allowClear && (mergedRawValue.length || mergedSearchValue)) {
            clearNode = (React.createElement(TransBtn, { className: `${prefixCls}-clear`, onMouseDown: onClearMouseDown, customizeIcon: clearIcon }, "\u00D7"));
        }
        const mergedShowArrow = showArrow !== undefined ? showArrow : loading || (!isMultiple && mode !== 'combobox');
        let arrowNode;
        if (mergedShowArrow) {
            arrowNode = (React.createElement(TransBtn, { className: classNames(`${prefixCls}-arrow`, {
                    [`${prefixCls}-arrow-loading`]: loading,
                }), customizeIcon: inputIcon, customizeIconProps: {
                    loading,
                    searchValue: mergedSearchValue,
                    open: mergedOpen,
                    focused: mockFocused,
                    showSearch: mergedShowSearch,
                } }));
        }
        if (process.env.NODE_ENV !== 'production' && warningProps) {
            warningProps(props);
        }
        const mergedClassName = classNames(prefixCls, className, {
            [`${prefixCls}-focused`]: mockFocused,
            [`${prefixCls}-multiple`]: isMultiple,
            [`${prefixCls}-single`]: !isMultiple,
            [`${prefixCls}-allow-clear`]: allowClear,
            [`${prefixCls}-show-arrow`]: mergedShowArrow,
            [`${prefixCls}-disabled`]: disabled,
            [`${prefixCls}-loading`]: loading,
            [`${prefixCls}-open`]: mergedOpen,
            [`${prefixCls}-customize-input`]: customizeInputElement,
            [`${prefixCls}-show-search`]: mergedShowSearch,
        });
        return (React.createElement("div", Object.assign({ className: mergedClassName }, domProps, { ref: containerRef, onMouseDown: onInternalMouseDown, onKeyDown: onInternalKeyDown, onKeyUp: onInternalKeyUp, onFocus: onContainerFocus, onBlur: onContainerBlur }),
            mockFocused && !mergedOpen && (React.createElement("span", { style: {
                    width: 0,
                    height: 0,
                    display: 'flex',
                    overflow: 'hidden',
                    opacity: 0,
                }, "aria-live": "polite" }, `${mergedRawValue.join(', ')}`)),
            React.createElement(SelectTrigger, { ref: triggerRef, disabled: disabled, prefixCls: prefixCls, visible: triggerOpen, popupElement: popupNode, containerWidth: containerWidth, animation: animation, transitionName: transitionName, dropdownStyle: dropdownStyle, dropdownClassName: dropdownClassName, direction: direction, dropdownMatchSelectWidth: dropdownMatchSelectWidth, dropdownRender: dropdownRender, dropdownAlign: dropdownAlign, getPopupContainer: getPopupContainer, empty: !mergedOptions.length, getTriggerDOMNode: () => selectorDomRef.current },
                React.createElement(Selector, Object.assign({}, props, { domRef: selectorDomRef, prefixCls: prefixCls, inputElement: customizeInputElement, ref: selectorRef, id: mergedId, showSearch: mergedShowSearch, mode: mode, accessibilityIndex: accessibilityIndex, multiple: isMultiple, tagRender: tagRender, values: displayValues, open: mergedOpen, onToggleOpen: onToggleOpen, searchValue: mergedSearchValue, activeValue: activeValue, onSearch: triggerSearch, onInputPaste: onPaste, onSearchSubmit: onSearchSubmit, onSelect: onInternalSelectionSelect, tokenWithEnter: tokenWithEnter }))),
            arrowNode,
            clearNode));
    }
    const RefSelect = React.forwardRef(Select);
    return RefSelect;
}
